import { Injectable } from '@angular/core';
import { resourceAdaptor } from 'qp-adaptor';
import { MetadataService, createMetadataService } from 'qp-storefront';
import {
  ContainerVm,
  Resource,
  ResourceList,
  ResourceVm,
} from 'qp-ui-interface';
import { AdaptorData } from 'src/app/storefront/AdaptorData';
import { ErrorFactory } from '../modules/errors/handler/ErrorFactory';
import { getDeviceType } from '../utils/deviceUtils';
import { ErrorEvents } from '../utils/ErrorUtils';
import { ConfigService } from './config.service';
import { StorefrontPolicyService } from './firstlight/storefront-policy/storefront-policy.service';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';

@Injectable({
  providedIn: 'root',
})
export class MetaDataService {
  private _instance: MetadataService;
  constructor(
    private readonly configService: ConfigService,
    private readonly policyService: StorefrontPolicyService,
    private readonly newrelicService: NewrelicService
  ) {}

  private init() {
    this._instance = createMetadataService(
      this.configService.applicationConfig.vodMetaDataURL,
      this.policyService.getPolicy()
    );
  }

  private service() {
    if (!this._instance) {
      this.init();
    }
    return this._instance;
  }

  refresh() {
    this._instance = null;
    this.init();
  }

  async getResources(ids: string[]) {
    try {
      return await this.service().getResources(ids);
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }

  async getResourceDetails(resourceType: string, resourceId: string) {
    try {
      return await this.service().getResourceDetails(resourceType, resourceId);
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }

  async getSeries(
    resourceId: string,
    pageNumber: number,
    pageSize: number,
    path: string,
    seriesPath: string
  ) {
    try {
      return await this.service().getSeries(
        resourceId,
        pageNumber,
        pageSize,
        path,
        seriesPath
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }

  async getEpisodes(
    resourceId: string,
    seasonId: string,
    pageNumber: string,
    pageSize: string,
    seriesPath: string,
    episodesPath: string
  ) {
    try {
      return await this.service().getEpisodes(
        resourceId,
        seasonId,
        pageNumber,
        pageSize,
        seriesPath,
        episodesPath
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }

  async fetchResourceVM(adaptorData: AdaptorData): Promise<ResourceVm> {
    try {
      const resource: Resource = await this.service().getResourceDetails(
        adaptorData.resourceType,
        adaptorData.resourceId
      );
      return resourceAdaptor(
        this.configService.applicationConfig.imageResizeURL,
        resource as unknown as Resource,
        getDeviceType(),
        '0-16x9',
        adaptorData.container,
        undefined,
        {
          storefrontId: adaptorData?.storefrontId,
          tabId: adaptorData?.tabId,
          tabName: adaptorData?.tabName,
          originScreen: adaptorData?.originScreen,
          collectionId: adaptorData?.collectionId,
          collectionTitle: adaptorData?.collectionTitle,
          containerName: adaptorData?.containerName,
          containerId: adaptorData?.containerId,
        }
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return Promise.reject(
        ErrorFactory.get(error, ErrorEvents.RESOURCE_FETCH_ERROR)
      );
    }
  }

  public async fetchViewMore(
    viewMoreUrl: string[],
    pageNumber: number = 1,
    pageSize: number = 10,
    container?: ContainerVm
  ): Promise<ContainerVm | null> {
    try {
      const response: ResourceList = await this.service().getViewMore(
        viewMoreUrl,
        pageNumber,
        pageSize
      );
      const resources = response.resources.map((resource) =>
        resourceAdaptor(
          this.configService.applicationConfig.imageResizeURL,
          resource,
          getDeviceType(),
          '0-2x3',
          undefined,
          undefined,
          {
            collectionId: container?.id,
            collectionTitle: container?.name,
          }
        )
      );
      return {
        id: container?.id,
        name: container?.name,
        layout: 'carousel',
        resources,
        contentCount: response.totalResources,
      };
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
    }
    return null;
  }

  public async fetchCollection(
    container: ContainerVm,
    viewMoreUrl: string[],
    pageNumber: number = 1,
    pageSize: number = 20
  ) {
    try {
      const response = await this.fetchViewMore(
        viewMoreUrl,
        pageNumber,
        pageSize,
        container
      );
      container.resources = response.resources;
      container.contentCount = response.contentCount;
      container.viewMoreUrl = viewMoreUrl;
      return Promise.resolve([container]);
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return Promise.reject(null);
    }
  }

  public async fetchUpNextContent(parameters: any) {
    try {
      return await this.service().getUpNext(parameters);
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
    }
    return null;
  }

  public async fetchViewMoreContainer(
    viewMoreUrl: string[],
    pageNumber: number,
    pageSize: number
  ) {
    try {
      return await this.service().getViewMore(
        viewMoreUrl,
        pageNumber,
        pageSize
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
    }
    return null;
  }
}
